import { FC, ReactElement, useEffect, useRef } from 'react';

import { useModifiers } from '@pp/core/src/styles/hooks/useModifiers';

import { airplaneStates } from '../../../store/slices/highflyerAirPlane/types';

import styles from './Airplane.module.css';

export interface AirplaneInterface {
  airplaneCurrentState: airplaneStates;
  isAnimating: boolean;
  motionID: number;
  airplaneWH: {
    airplaneBoxWidth: number;
    airplaneBoxHeight: number;
    planeWidth: number;
    PlaneHeight: number;
    flagWidth: number;
    flagHeight: number;
    scaleValue: number;
  };
  inDesktopMode: boolean;
}

export const Parabola: FC<AirplaneInterface> = ({
  airplaneCurrentState,
  isAnimating,
  motionID,
  airplaneWH,
  inDesktopMode,
}): ReactElement | null => {
  const line = useRef<SVGAnimationElement | null>(null);
  const lineBG = useRef<SVGAnimationElement | null>(null);
  const pathRef = useRef<SVGAnimationElement | null>(null);
  const pathBGRef = useRef<SVGAnimationElement | null>(null);

  const startPoint = useRef([1 * airplaneWH.scaleValue, airplaneWH.airplaneBoxHeight - 15 * airplaneWH.scaleValue]);
  const controlPoint = useRef([airplaneWH.airplaneBoxWidth * 0.6, airplaneWH.airplaneBoxHeight]);
  const endPoint = useRef([
    inDesktopMode
      ? airplaneWH.airplaneBoxWidth - airplaneWH.planeWidth
      : airplaneWH.airplaneBoxWidth - airplaneWH.planeWidth - 40,
    airplaneWH.PlaneHeight,
  ]);
  const endPointAnimate = useRef([
    inDesktopMode
      ? airplaneWH.airplaneBoxWidth - airplaneWH.planeWidth
      : airplaneWH.airplaneBoxWidth - airplaneWH.planeWidth - 40,
    airplaneWH.PlaneHeight + 100 * airplaneWH.scaleValue,
  ]);

  const rootStyles = getComputedStyle(document.documentElement);
  const alpha = 0.6;
  const GColor = rootStyles.getPropertyValue('--cssPlaneLineColor').trim().slice(1, -1).replace(/"/g, '');
  const graphline = `rgba(${GColor}, 1)`;
  const graphBackGround = `rgba(${GColor}, ${alpha})`;

  useEffect(() => {
    if (!line.current || !lineBG.current || !pathRef.current || !pathBGRef.current) return;
    if (!inDesktopMode && airplaneCurrentState === airplaneStates.FLYING) {
      line.current.beginElement();
      lineBG.current.beginElement();
    }
  }, [airplaneCurrentState, airplaneWH]);

  useEffect(() => {
    if (!line.current || !lineBG.current || !pathRef.current || !pathBGRef.current) return;
    if (inDesktopMode && airplaneCurrentState === airplaneStates.FLYING) {
      line.current.beginElement();
      lineBG.current.beginElement();
    }
  }, [airplaneCurrentState, inDesktopMode]);

  useEffect(() => {
    startPoint.current = [1 * airplaneWH.scaleValue, airplaneWH.airplaneBoxHeight - 15 * airplaneWH.scaleValue];
    controlPoint.current = [airplaneWH.airplaneBoxWidth * 0.6, airplaneWH.airplaneBoxHeight];
    endPoint.current = [
      inDesktopMode
        ? airplaneWH.airplaneBoxWidth - airplaneWH.planeWidth
        : airplaneWH.airplaneBoxWidth - airplaneWH.planeWidth - 40,
      airplaneWH.PlaneHeight,
    ];
    endPointAnimate.current = [
      inDesktopMode
        ? airplaneWH.airplaneBoxWidth - airplaneWH.planeWidth
        : airplaneWH.airplaneBoxWidth - airplaneWH.planeWidth - 40,
      airplaneWH.PlaneHeight + 100 * airplaneWH.scaleValue,
    ];
  }, [airplaneWH]);


  const mods = useModifiers(styles, 'graph', {
    [airplaneCurrentState]: true,
  });

  if (!isAnimating) return null;
  return (
    <>
      <g id="anim">
        <g xmlns="http://www.w3.org/2000/svg" id="graph" className={`${styles.graph} ${mods}`}>
          <path
            clipPath={`url(#${motionID.toString()}_0)`}
            id="Graph_2"
            d="M 0,482"
            fill="url(#paint0_linear_135_2969)"
          >
            <animate
              ref={pathBGRef}
              attributeName="d"
              values={`
              M ${startPoint.current}
              Q ${controlPoint.current} ${endPoint.current}
              V ${airplaneWH.airplaneBoxHeight} H0 Z ; 

              M ${startPoint.current}
              Q ${controlPoint.current} ${endPointAnimate.current}
              V ${airplaneWH.airplaneBoxHeight} H0 Z ;

              M ${startPoint.current}
              Q ${controlPoint.current} ${endPoint.current}
              V ${airplaneWH.airplaneBoxHeight} H0 Z 
            `}
              dur="6s"
              begin="0"
              repeatCount="indefinite"
              fill="freeze"
            />
          </path>
          <g id="Graph stroke">
            <path
              clipPath={`url(#${motionID.toString()}_1)`}
              id={motionID.toString()}
              className={styles.motionPath}
              d="M 55,478"
              stroke="url(#paint1_linear_135_2969)"
              strokeWidth={3.4 * airplaneWH.scaleValue}
            >
              <animate
                ref={pathRef}
                attributeName="d"
                values={`
                M ${startPoint.current}
                Q ${controlPoint.current} ${endPoint.current} ; 

                M ${startPoint.current}
                Q ${controlPoint.current} ${endPointAnimate.current} ;

                M ${startPoint.current}
                Q ${controlPoint.current} ${endPoint.current}
              `}
                dur="6s"
                begin="0"
                repeatCount="indefinite"
                fill="freeze"
              />
            </path>
          </g>
        </g>
      </g>
      <defs>
        <linearGradient
          id="paint0_linear_135_2969"
          x1={airplaneWH.airplaneBoxWidth * 0.99}
          y1={airplaneWH.airplaneBoxHeight * 0.5}
          x2={airplaneWH.airplaneBoxWidth * 0.2}
          y2={airplaneWH.airplaneBoxHeight * 0.5}
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={graphBackGround} stopOpacity="1" />
          <stop offset="1" stopColor={graphBackGround} stopOpacity="0" />
        </linearGradient>
        <linearGradient
          id="paint1_linear_135_2969"
          x1={airplaneWH.airplaneBoxWidth * 0.5}
          y1={airplaneWH.airplaneBoxHeight * 0.45}
          x2={airplaneWH.airplaneBoxWidth * 0.15}
          y2={airplaneWH.airplaneBoxHeight * 0.45}
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={graphline} />
          <stop offset="1" stopColor={graphline} stopOpacity="0" />
        </linearGradient>

        <clipPath id={`${motionID.toString()}_0`}>
          <rect
            id="rect"
            x="-83%"
            y="0"
            width={airplaneWH.airplaneBoxWidth / 1.2}
            height={airplaneWH.airplaneBoxHeight}
          />
        </clipPath>
        <clipPath id={`${motionID.toString()}_1`}>
          <rect
            id="rect1"
            x="-83.3%"
            y="0"
            width={airplaneWH.airplaneBoxWidth / 1.2}
            height={airplaneWH.airplaneBoxHeight}
          />
        </clipPath>
      </defs>

      <animateMotion
        ref={lineBG}
        attributeType="XML"
        xlinkHref="#rect"
        attributeName="motion"
        dur="3s"
        begin="0"
        fill="freeze"
        end={airplaneCurrentState === airplaneStates.FLEW_AWAY ? '0s' : ''}
        keyPoints="0;1;1"
        keyTimes="0;1;1"
        calcMode="linear"
      >
        <mpath xlinkHref={`#${motionID.toString()}`}></mpath>
      </animateMotion>
      <animateMotion
        ref={line}
        attributeType="XML"
        xlinkHref="#rect1"
        attributeName="motion"
        dur="3s"
        begin="0"
        fill="freeze"
        end={airplaneCurrentState === airplaneStates.FLEW_AWAY ? '0s' : ''}
        keyPoints="0;1;1"
        keyTimes="0;1;1"
        calcMode="linear"
      >
        <mpath xlinkHref={`#${motionID.toString()}`}></mpath>
      </animateMotion>
    </>
  );
};
